import type { PropsWithChildren } from "react";

import { styled } from "@linaria/react";

import { getCssSizeValue } from "../utils/linaria";

export enum GridAlignItems {
  START = "START",
  CENTER = "CENTER",
  END = "END",
  STRETCH = "STRETCH",
}

export enum GridJustifyItems {
  START = "START",
  CENTER = "CENTER",
  END = "END",
  STRETCH = "STRETCH",
}

export enum GridJustifyContent {
  START = "START",
  CENTER = "CENTER",
  END = "END",
  SPACE_BETWEEN = "SPACE_BETWEEN",
  SPACE_AROUND = "SPACE_AROUND",
  SPACE_EVENLY = "SPACE_EVENLY",
  STRETCH = "STRETCH",
}

export enum GridAlignContent {
  START = "START",
  CENTER = "CENTER",
  END = "END",
  SPACE_BETWEEN = "SPACE_BETWEEN",
  SPACE_AROUND = "SPACE_AROUND",
  SPACE_EVENLY = "SPACE_EVENLY",
  STRETCH = "STRETCH",
}

interface GridWrapperProps {
  /**
   * Number of columns in the grid, or a custom grid-template-columns value
   * - number: Creates that many equal-width columns using repeat(n, 1fr)
   * - string: Used directly as grid-template-columns value (e.g., "200px 1fr 1fr")
   */
  columns?: number | string;

  /**
   * Number of rows in the grid, or a custom grid-template-rows value
   * - number: Creates that many equal-height rows using repeat(n, 1fr)
   * - string: Used directly as grid-template-rows value
   */
  rows?: number | string;

  /**
   * Gap between grid items (both row and column gap)
   * Can be a number (pixels) or string (e.g., "1rem")
   */
  gap?: number | string;

  /**
   * Gap between rows only
   */
  rowGap?: number | string;

  /**
   * Gap between columns only
   */
  columnGap?: number | string;

  /**
   * Aligns grid items along the block (column) axis within their grid area
   */
  alignItems?: GridAlignItems;

  /**
   * Aligns grid items along the inline (row) axis within their grid area
   */
  justifyItems?: GridJustifyItems;

  /**
   * Aligns the grid along the block (column) axis when grid is smaller than container
   */
  alignContent?: GridAlignContent;

  /**
   * Aligns the grid along the inline (row) axis when grid is smaller than container
   */
  justifyContent?: GridJustifyContent;

  /**
   * Sets the width of the grid container
   */
  width?: number | string;

  /**
   * Sets the height of the grid container
   */
  height?: number | string;

  /**
   * Sets the minimum width of the grid container
   */
  minWidth?: number | string;

  /**
   * Sets the maximum width of the grid container
   */
  maxWidth?: number | string;

  /**
   * Sets the minimum height of the grid container
   */
  minHeight?: number | string;

  /**
   * Sets the maximum height of the grid container
   */
  maxHeight?: number | string;

  /**
   * When true, the container will fill its parent's width (width: 100%)
   */
  fillWidth?: boolean;

  /**
   * When true, the container will fill its parent's height (height: 100%)
   */
  fillHeight?: boolean;

  /**
   * Sets the padding inside the container
   */
  padding?: number | string;

  /**
   * Sets the margin outside the container
   */
  margin?: number | string;

  /**
   * Controls overflow behavior
   */
  overflow?: React.CSSProperties["overflow"];
}

const StyledGridWrapper = styled.div<GridWrapperProps>`
  display: grid;
  box-sizing: border-box;

  grid-template-columns: ${({ columns = 1 }) =>
    typeof columns === "number" ? `repeat(${columns}, 1fr)` : columns};

  grid-template-rows: ${({ rows }) =>
    rows ? (typeof rows === "number" ? `repeat(${rows}, 1fr)` : rows) : "auto"};

  gap: ${({ gap }) => getCssSizeValue(gap)};
  row-gap: ${({ rowGap, gap }) => getCssSizeValue(rowGap ?? gap)};
  column-gap: ${({ columnGap, gap }) => getCssSizeValue(columnGap ?? gap)};

  align-items: ${({ alignItems = GridAlignItems.STRETCH }) => alignItems};
  justify-items: ${({ justifyItems = GridJustifyItems.STRETCH }) => justifyItems};
  align-content: ${({ alignContent = GridAlignContent.START }) => alignContent};
  justify-content: ${({ justifyContent = GridJustifyContent.START }) => justifyContent};

  width: ${({ width, fillWidth }) => {
    if (fillWidth) return "100%";
    if (width) return getCssSizeValue(width);
    return "auto";
  }};
  height: ${({ height, fillHeight }) => {
    if (fillHeight) return "100%";
    if (height) return getCssSizeValue(height);
    return "auto";
  }};
  min-width: ${({ minWidth }) => (minWidth ? getCssSizeValue(minWidth) : "auto")};
  max-width: ${({ maxWidth }) => (maxWidth ? getCssSizeValue(maxWidth) : "none")};
  min-height: ${({ minHeight }) => (minHeight ? getCssSizeValue(minHeight) : "auto")};
  max-height: ${({ maxHeight }) => (maxHeight ? getCssSizeValue(maxHeight) : "none")};

  overflow: ${({ overflow = "visible" }) => overflow};
  padding: ${({ padding }) => (padding ? getCssSizeValue(padding) : "0")};
  margin: ${({ margin }) => (margin ? getCssSizeValue(margin) : "0")};
`;

const GridWrapper = ({
  children,
  columns,
  rows,
  gap,
  rowGap,
  columnGap,
  alignItems,
  justifyItems,
  alignContent,
  justifyContent,
  width,
  height,
  minWidth,
  maxWidth,
  minHeight,
  maxHeight,
  fillWidth = false,
  fillHeight = false,
  padding,
  margin,
  overflow,
}: PropsWithChildren<GridWrapperProps>) => {
  return (
    <StyledGridWrapper
      columns={columns}
      rows={rows}
      gap={gap}
      rowGap={rowGap}
      columnGap={columnGap}
      alignItems={alignItems}
      justifyItems={justifyItems}
      alignContent={alignContent}
      justifyContent={justifyContent}
      width={width}
      height={height}
      minWidth={minWidth}
      maxWidth={maxWidth}
      minHeight={minHeight}
      maxHeight={maxHeight}
      fillWidth={fillWidth}
      fillHeight={fillHeight}
      padding={padding}
      margin={margin}
      overflow={overflow}
    >
      {children}
    </StyledGridWrapper>
  );
};

export default GridWrapper;
